import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Raw node data for lazy hydration — kept as a plain Map (not React state).
 */
export const rawInstanceNodeMap = new Map<string, any>();

type ProcedureInstanceState = {
  metadata: any;
  rootChildIds: string[];
  /** Per-node data keyed by _id. Replaces atomFamily. */
  nodes: Record<string, any>;
  isDirty: boolean;
  nodeIds: string[];
  /** Map<nodeId, isFilled> — tracks required field completion. */
  requiredFieldStatus: Map<string, boolean>;
  /** Ids of the required sub-asset (ASSETS_FIELD) nodes. */
  requiredSubAssetFieldIds: string[];
  /** Asset (_id) of the work order this procedure belongs to, if any. */
  assetId: string | null;

  setMetadata: (metadata: any) => void;
  updateMetadata: (patch: Partial<any>) => void;
  setRootChildIds: (ids: string[]) => void;
  setNode: (nodeId: string, data: any) => void;
  updateNodeValue: (
    nodeId: string,
    newValue: any,
    isRequired: boolean,
    isFilled: boolean,
  ) => void;
  setNodes: (nodes: Record<string, any>) => void;
  setIsDirty: (dirty: boolean) => void;
  setNodeIds: (ids: string[]) => void;
  setRequiredFieldStatus: (status: Map<string, boolean>) => void;
  setAssetId: (assetId: string | null) => void;
  /** Batch initialize all state from parsed procedure instance data. */
  initialize: (opts: {
    metadata: any;
    nodes: Record<string, any>;
    rootChildIds: string[];
    nodeIds: string[];
    requiredFieldStatus: Map<string, boolean>;
    requiredSubAssetFieldIds: string[];
  }) => void;
  /** Reset all state on unmount. */
  reset: () => void;
};

const INITIAL_STATE = {
  metadata: null as any,
  rootChildIds: [] as string[],
  nodes: {} as Record<string, any>,
  isDirty: false,
  nodeIds: [] as string[],
  requiredFieldStatus: new Map<string, boolean>(),
  requiredSubAssetFieldIds: [] as string[],
  assetId: null as string | null,
};

export const useProcedureInstanceStore = create<ProcedureInstanceState>()(
  devtools(
    (set) => ({
      ...INITIAL_STATE,

      setMetadata: (metadata) => set({ metadata }),
      updateMetadata: (patch) =>
        set((s) => ({ metadata: { ...s.metadata, ...patch } })),
      setRootChildIds: (rootChildIds) => set({ rootChildIds }),
      setNode: (nodeId, data) =>
        set((s) => ({ nodes: { ...s.nodes, [nodeId]: data } })),
      updateNodeValue: (nodeId, newValue, isRequired, isFilled) =>
        set((s) => {
          const updatedNodes = {
            ...s.nodes,
            [nodeId]: s.nodes[nodeId]
              ? { ...s.nodes[nodeId], value: newValue }
              : { value: newValue },
          };
          if (!isRequired) {
            return { nodes: updatedNodes, isDirty: true };
          }
          const newMap = new Map(s.requiredFieldStatus);
          newMap.set(nodeId, isFilled);
          return {
            nodes: updatedNodes,
            isDirty: true,
            requiredFieldStatus: newMap,
          };
        }),
      setNodes: (nodes) => set({ nodes }),
      setIsDirty: (isDirty) => set({ isDirty }),
      setNodeIds: (nodeIds) => set({ nodeIds }),
      setRequiredFieldStatus: (requiredFieldStatus) =>
        set({ requiredFieldStatus }),
      setAssetId: (assetId) => set({ assetId }),

      initialize: ({
        metadata,
        nodes,
        rootChildIds,
        nodeIds,
        requiredFieldStatus,
        requiredSubAssetFieldIds,
      }) =>
        set({
          metadata,
          nodes,
          rootChildIds,
          nodeIds,
          requiredFieldStatus,
          requiredSubAssetFieldIds,
          isDirty: false,
        }),

      reset: () =>
        set({
          ...INITIAL_STATE,
          requiredFieldStatus: new Map(),
          requiredSubAssetFieldIds: [],
        }),
    }),
    { name: "ProcedureInstanceStore" },
  ),
);
